import clsx from 'clsx';
import type { Editor } from '@tiptap/react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Divider } from '@flintmint/web-shared/components/divider';
import { Popover } from '@flintmint/web-shared/components/popover';
import { LastSyncEditTime } from '@/components/last-sync-edit-time';
import { type MenuData } from '@flintmint/web-shared/components/menu';
import { InlineInput } from '@flintmint/web-shared/components/inline-input';
import type { RenderAlert } from '@flintmint/web-shared/hooks/use-close-confirmation';

import styles from './simple-document-header.module.scss';
import { itemTypes } from '@/constants/item-types';
import { TagsSet } from '@/components/tags-set';
import { RxDocument } from 'rxdb';
import { Block, Instruction, Note, UserSource } from '@flintmint/core-shared/rxdb';
import { ReactNode } from 'react';
import { CopyToClipboardBtn } from '@/components/copty-to-clipboard-btn';
import { MAX_DOCUMENT_NAME_LENGTH } from '@/extensions/content-size-limit';

export const SimpleDocumentHeader = (props: SimpleDocumentHeaderProps) => {
  const {
    data,
    type,
    menu,
    size,
    tagIds,
    actions,
    onClose,
    editor,
    className,
    renderAlert,
    onNameChange,
    documentName,
    onTagsUpdate,
    closeWithAlert,
    closeFromRight,
    withDivider = true,
    ...rest
  } = props;

  const renderCloseBtn =
    closeWithAlert && !!renderAlert ? (
      renderAlert(<Icon type="cancel-01" data-testid="close-icon-popover" size={24} className={styles.closeIcon} />, false)
    ) : (
      <Icon size={24} type="cancel-01" data-testid="close-icon" className={styles.closeIcon} onClick={onClose} />
    );

  return (
    <div className={clsx(styles.simpleDocumentHeaderHolder, styles[`s-${size}`], className)} {...rest}>
      <div className={styles.simpleDocumentHeader}>
        <div className={styles.meta}>
          {!closeFromRight && (
            <>
              {renderCloseBtn}
              <Divider orientation="vertical" className={styles.mainDivider} />
            </>
          )}
          <ul>
            <li>
              {!!type && (
                <p className={styles.typeChip} data-testid="type-chip">
                  <Icon type={itemTypes[type!].icon} size={16} />
                  <span>{itemTypes[type!]?.name}</span>
                </p>
              )}
              <div className={styles.nameHolder}>
                <InlineInput
                  value={documentName}
                  readOnly={!onNameChange}
                  maxLength={MAX_DOCUMENT_NAME_LENGTH}
                  placeholder="Untitled Block"
                  className={styles.inlineInput}
                  data-testid={`${type ?? 'data'}-name`}
                  onChange={(e) => onNameChange?.(e.target.value)}
                />
                {!!menu && (
                  <Popover menu={menu} placement="bottom">
                    <Icon type="more-horizontal" className={styles.menuIcon} data-testid="menu-icon" />
                  </Popover>
                )}
              </div>
            </li>
            <li className={styles.updatedDateAndTags}>
              {!!data && (
                <LastSyncEditTime
                  className={styles.lastSync}
                  lastEditTime={data?.updatedAt}
                  lastSyncTime={(data as UserSource)?.lastSyncAt}
                />
              )}
              <TagsSet onChange={onTagsUpdate} tagIds={tagIds || data?.tagIds} />
              {editor && <CopyToClipboardBtn editor={editor} />}
            </li>
          </ul>
        </div>
        {!!actions && <div className={styles.actions}>{actions}</div>}
        {!!closeFromRight && <div className={styles.rightClose}>{renderCloseBtn}</div>}
      </div>
      {withDivider && <Divider orientation="horizontal" />}
    </div>
  );
};

export interface SimpleDocumentHeaderProps {
  type?: string;
  menu?: MenuData;
  tagIds?: string[];
  className?: string;
  actions?: ReactNode;
  onClose?: () => void;
  documentName: string;
  withDivider?: boolean;
  editor?: Editor | null;
  closeWithAlert?: boolean;
  closeFromRight?: boolean;
  renderAlert?: RenderAlert;
  size?: 'small' | 'default';
  onNameChange?: (name: string) => void;
  onTagsUpdate: (tagIds: string[]) => void;
  data?: RxDocument<Note> | RxDocument<UserSource> | RxDocument<Instruction> | RxDocument<Block> | Block;
}
